// RunTab.js
import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    fetchBuildData,
    getDevicesPerPlatform,
    triggerTestExecution,
    clients,
    suites,
    fetchJenkinsPlans,
    baseURL
} from './DataFlow';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { dateTemplate } from './DataFlow';
import { InputNumber } from 'primereact/inputnumber';
import { FilterService, FilterMatchMode } from 'primereact/api';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';

const RunTab = () => {
    const [selectedBuilds, setSelectedBuilds] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [branchPattern, setBranchPattern] = useState('');
    const [versionSearch, setVersionSearch] = useState('');
    const [selectedBuild, setSelectedBuild] = useState(null);
    const [selectedSuite, setSelectedSuite] = useState(null);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [infrastructure, setInfrastructure] = useState([]);
    const [statusExecution, setExecutionStatus] = useState(null);
    const [powerUser, setPowerUser] = useState(false);
    const [jenkinsPlans, setJenkinsPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paramValues, setParamValues] = useState({});

    const [buildRows, setBuildRows] = useState(5);
    const [buildFirst, setBuildFirst] = useState(0);
    const [deviceRows, setDeviceRows] = useState(5);
    const [deviceFirst, setDeviceFirst] = useState(0);
    const [hasFetched, setHasFetched] = useState(false);

    // Initialize filters
    const [deviceFilters, setDeviceFilters] = useState({
        agentName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        model: { value: null, matchMode: FilterMatchMode.CONTAINS },
        platform: { value: null, matchMode: FilterMatchMode.CONTAINS },
        os: { value: null, matchMode: FilterMatchMode.CONTAINS },
        ram: { value: null, matchMode: 'custom' },
        enabled: { value: null, matchMode: FilterMatchMode.EQUALS },
        comments: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.EQUALS },
    });

    const isPowerUser = localStorage.getItem('isAppPowerUser') === 'true';
    const toast = useRef(null);

    useEffect(() => {
        if (!hasFetched) {
            fetchBuildData(
                selectedClient,
                branchPattern,
                versionSearch,
                setSelectedBuilds,
                setInfrastructure,
                setPowerUser
            );
            setHasFetched(true);
        }
    }, [selectedClient, branchPattern, versionSearch, powerUser, statusExecution, hasFetched]);

    useEffect(() => {
        fetchJenkinsPlans(setJenkinsPlans);
    }, []);

    // Custom filter example for "ram" range
    FilterService.register('custom_ram', (value, filter) => {
        let from = null;
        let to = null;

        if (filter) {
            from = filter.from;
            to = filter.to;
        }
        if (from === null && to === null) return true;
        if (from !== null && to === null) return from <= value;
        if (from === null && to !== null) return value <= to;
        return from <= value && value <= to;
    });

    const onBuildsPageChange = (e) => {
        setBuildFirst(e.first);
        setBuildRows(e.rows);
    };

    const onDevicesPageChange = (e) => {
        setDeviceFirst(e.first);
        setDeviceRows(e.rows);
    };

    const showNotificationRun = (toast, selectedSuite, selectedBuild) => {
        const cleanSelectedDevices = selectedDevices.filter((device) => device !== undefined);

        if (selectedSuite && selectedBuild && cleanSelectedDevices.length > 0) {
            toast.current.show({
                severity: 'success',
                summary: 'Scheduled',
                detail: 'Build executed!',
                life: 10000
            });
            triggerTestExecution(selectedSuite, selectedBuild, cleanSelectedDevices, setExecutionStatus);
        } else {
            let error = '';

            if (!selectedSuite) {
                error = 'Select suite!';
            } else if (!selectedBuild) {
                error = 'Select build!';
            } else if (cleanSelectedDevices.length === 0) {
                error = 'Select at least one device!';
            }

            toast.current.show({ severity: 'error', summary: 'Error', detail: error, life: 10000 });
        }
    };

    const onBuildSelectionChange = (e) => {
        setSelectedBuild(e.value);
        if (e.value != null) {
            setSelectedDevices([]); // Reset selected devices
            getDevicesPerPlatform(e.value, setInfrastructure);
        }
    };

    // Updated rangeFilter component
    const rangeFilterTemplate = (options) => {
        const from = options.value?.from || null;
        const to = options.value?.to || null;

        return (
            <div className="flex gap-1">
                <InputNumber
                    value={from}
                    onChange={(e) => {
                        options.filterApplyCallback({ from: e.value, to });
                    }}
                    placeholder="Min"
                />
                <InputNumber
                    value={to}
                    onChange={(e) => {
                        options.filterApplyCallback({ from, to: e.value });
                    }}
                    placeholder="Max"
                />
            </div>
        );
    };

    const enabledOptions = [
        { label: 'True', value: true },
        { label: 'False', value: false }
    ];

    const typeOptions = [
        { label: 'On-premise', value: 'On-premise' },
        { label: 'Cloud', value: 'Cloud' }
    ];

    const handlePlanSelection = async (plan) => {
        console.log('Selected plan:', plan);
        setSelectedPlan(plan);
        // Reset all selections when plan changes
        setSelectedBuild(null);
        setSelectedDevices([]);
        
        if (plan && plan.parameters) {
            // Initialize parameter values with defaults
            const initialValues = {};
            plan.parameters.forEach(param => {
                // Initialize CascadeChoiceParameter as empty array
                if (param.type === 'CascadeChoiceParameter') {
                    initialValues[param.name] = [];
                } else {
                    initialValues[param.name] = param.defaultParameterValue?.value || '';
                }
            });
            setParamValues(initialValues);
        } else {
            setParamValues({});
        }
    };

    const handleParamValueChange = (name, value, type) => {
        setParamValues(prev => ({
            ...prev,
            [name]: value
        }));

        // Special handling for Build parameter
        if (type === 'Builds' && value) {
            setSelectedBuild(value);
            // Reset devices when build changes
            setSelectedDevices([]);
            // Fetch devices for the selected build
            getDevicesPerPlatform(value, setInfrastructure);
        }
    };

    const renderParameterField = (param) => {
        console.log('Rendering parameter:', param);
        const { name, type, defaultParameterValue } = param;
        const value = paramValues[name] || [];  // Initialize as empty array

        switch (type) {
            case 'StringParameterDefinition':
                return (
                    <div className="p-field mb-3">
                        <label htmlFor={name}>{name}</label>
                        <InputText
                            id={name}
                            value={value}
                            onChange={(e) => handleParamValueChange(name, e.target.value)}
                            className="w-full"
                        />
                    </div>
                );

            case 'BooleanParameterDefinition':
                return (
                    <div className="p-field mb-3">
                        <div className="flex align-items-center">
                            <Checkbox
                                inputId={name}
                                checked={value === 'true' || value === true}
                                onChange={(e) => handleParamValueChange(name, e.checked)}
                            />
                            <label htmlFor={name} className="ml-2">
                                {name}
                            </label>
                        </div>
                    </div>
                );

            case 'CascadeChoiceParameter':
                return (
                    <div className="p-field mb-3">
                        <label htmlFor={name}>{name}</label>
                        <MultiSelect
                            id={name}
                            value={Array.isArray(value) ? value : []}  // Ensure value is always an array
                            options={param.choices?.map(choice => ({ label: choice, value: choice })) || []}
                            onChange={(e) => handleParamValueChange(name, e.value)}
                            className="w-full"
                            style={{ minWidth: '300px' }}
                            placeholder="Select values"
                            filter
                            showClear
                        />
                    </div>
                );

            case 'Builds':
                return (
                    <Card>
                        <div className="p-field">
                            <div className="my-3 border-bottom-1 border-gray-200">
                                <label htmlFor={name} className="text-xl">Build Selection</label>
                            </div>
                            <DataTable
                                value={selectedBuilds}
                                showGridlines
                                selection={selectedBuild}
                                onSelectionChange={(e) => handleParamValueChange(name, e.value, type)}
                                // If your builds also have "id" instead of "_id", change this accordingly
                                dataKey="_id"
                                className="w-full"
                                paginator
                                rows={5}
                                first={buildFirst}
                                onPage={onBuildsPageChange}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                            >
                                <Column selectionMode="single" headerStyle={{ width: '3rem' }} />
                                <Column field="buildNo" header="Build" sortable />
                                <Column field="gameName" header="Game" sortable />
                                <Column field="platform" header="Platform" sortable />
                                <Column field="branch" header="Branch" sortable />
                                <Column field="version" header="Version" sortable />
                            </DataTable>
                        </div>
                    </Card>
                );

            case 'Device':
                // Multi-selection DataTable (checkbox-based)
                return (
                    <Card>
                            <div className="p-field">
                                <div className="my-3 border-bottom-1 border-gray-200">
                                    <label htmlFor={name} className="text-xl">Device Selection</label>
                            </div>
                            <DataTable
                                value={infrastructure}
                                selection={selectedDevices}
                                showGridlines
                                onSelectionChange={(e) => {
                                    setSelectedDevices(e.value);
                                    // Notice we map to 'device.id' because your device objects have "id" (not "_id")
                                    handleParamValueChange(name, e.value.map(device => device.id), type);
                                }}
                                dataKey="id"         // IMPORTANT: match the actual field in your data
                                selectionMode="checkbox"
                                className="w-full"
                                paginator
                                rows={5}
                                first={deviceFirst}
                                onPage={onDevicesPageChange}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                            >
                                <Column
                                    selectionMode="multiple"
                                    headerStyle={{ width: '3rem' }}
                                    selectionAriaLabel="device selection"
                                />
                                <Column field="agentName" header="Agent" sortable />
                                <Column field="model" header="Model" sortable />
                                <Column field="platform" header="Platform" sortable />
                                <Column field="os" header="OS" sortable />
                            </DataTable>
                        </div>
                    </Card>
                );

            case 'ChoiceParameterDefinition':
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <Dropdown
                            id={name}
                            value={value}
                            options={param.choices?.map(choice => ({ label: choice, value: choice })) || []}
                            onChange={(e) => handleParamValueChange(name, e.value)}
                            className="w-full"
                        />
                    </div>
                );

            default:
                return (
                    <div className="p-field">
                        <label htmlFor={name}>{name}</label>
                        <InputText
                            id={name}
                            value={value}
                            onChange={(e) => handleParamValueChange(name, e.target.value)}
                            className="w-full"
                        />
                    </div>
                );
        }
    };

    const handleRunTest = async () => {
        if (!selectedPlan) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a plan'
            });
            return;
        }

        try {
            const testConfig = {
                planId: selectedPlan._id,
                parameters: paramValues,
                origin: 'manual'
            };

            const response = await fetch(`${baseURL}/api/tests/run-with-params`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'api_token': localStorage.getItem('apiToken')
                },
                body: JSON.stringify(testConfig)
            });

            if (!response.ok) {
                throw new Error('Failed to start test');
            }

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Test started successfully'
            });

            // Reset form
            setSelectedBuild(null);
            setSelectedDevices([]);
            setParamValues({});
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message
            });
        }
    };

    // If not a power user, show message
    if (!isPowerUser) {
        return (
            <div className="card">
                <div className="flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                    <div className="text-center">
                        <i className="pi pi-lock text-5xl mb-3" style={{ color: '#FFA726' }}></i>
                        <div className="text-2xl font-bold mb-2">Power User Access Required</div>
                        <div className="text-gray-600">You need Power User permissions to access the Run Tab.</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Toast ref={toast} />
            
            <h2>Plan Selection</h2>
            <Card>
                <Dropdown
                    value={selectedPlan}
                    options={jenkinsPlans}
                    onChange={(e) => handlePlanSelection(e.value)}
                    optionLabel="name"
                    placeholder="Select a Jenkins Plan"
                    className="w-full"
                />
            </Card>

            {selectedPlan && selectedPlan.parameters && (
                <>
                    <h2>Parameters</h2>
                    <Card>
                        {selectedPlan.parameters.map((param, index) => (
                            <div key={index} className="mb-3">
                                {renderParameterField(param)}
                            </div>
                        ))}
                    </Card>

                    <div className="mt-3">
                        <Button
                            label="Run Test"
                            icon="pi pi-play"
                            onClick={handleRunTest}
                            disabled={!powerUser}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default RunTab;
